// Игра «Кто победит?» (§2.12). Читать можно без Bearer; с ним приходит my_pick — поэтому сессия в ключе запроса.
// Голос (PUT) и снятие (DELETE) — с Bearer: гостевая сессия заводится лениво, гость — настоящий игрок.
// Ответ PUT/DELETE — авторитетный блок, рисуем его. 409 prediction_closed — матч начался: перечитываем блок.
// Пояс для суток серии — tz (IANA) в теле PUT и ?tz= у чтений (с 09.10 сервер принимает и заголовок X-Timezone — им пользуется мир).
import { type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest, withParams } from './client';
import { predictionResult } from '../analytics/events';
import { track } from '../analytics/track';
import type { FriendLeague, FriendLeagueRow, GameCheer, GameFixture, GameOverQuestion, GameScreen, Leaderboard } from './gameTypes';
import { useHasSession } from '../session/favorites';

const SPORT = 'cricket';
const fixturePath = (id: number) => `/api/v5/mobile/game/${SPORT}/fixtures/${id}/`;
const predictionPath = (id: number) => `/api/v5/mobile/game/${SPORT}/predictions/${id}/`;
const key = (id: number) => ['game-fixture', id];
const SCREEN_KEY = ['game-screen'];

/** IANA-пояс устройства (Asia/Kolkata); нет — сервер посчитает по запомненному или UTC. */
export function deviceTimeZone(): string | undefined {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { return undefined; }
}

/** Блок матча; null — матча в игре нет (404 not_found): блок не рисуем. */
/** path — адрес сервера как есть (блок `game.endpoint` экрана матча); нет — собираем по номеру. */
/** enabled: false — не грузить (вопросы матча в списке игры — по раскрытию).
 *  livePollMs — частота опроса экрана матча (poll.interval_seconds): пока в ответе есть `live`, блок перечитываем
 *  с ней, но не чаще cheer.refresh_seconds — так приходит вопрос про следующий овер и свежая шкала. */
export function useGameFixture(id: number, path?: string, enabled = true, livePollMs?: number) {
  const has = useHasSession();
  return useQuery({
    queryKey: [...key(id), has],
    queryFn: async ({ signal }) => {
      try {
        return await apiRequest<GameFixture>('GET', path ?? fixturePath(id), { auth: has, signal }) as GameFixture | null;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    enabled,
    refetchInterval: (query) => liveRefetchMs(query.state.data ?? undefined, livePollMs),
  });
}

/** Опрос блока идущего матча: нет live — не опрашиваем; иначе частота матча (запасная 15 с), не чаще refresh_seconds. */
export function liveRefetchMs(fixture: GameFixture | undefined, livePollMs?: number): number | false {
  if (!fixture?.live) return false;
  const floor = (fixture.live.cheer?.refresh_seconds ?? 5) * 1000;
  return Math.max(livePollMs ?? 15_000, floor, 5_000);
}

/** Адрес действия идущего матча: от адреса блока матча (без query) — fixtures/{id}/over/ и /cheer/. */
export function liveActionPath(fixtureUrl: string, action: 'over' | 'cheer'): string {
  const [base = ''] = fixtureUrl.split('?');
  return `${base.endsWith('/') ? base : `${base}/`}${action}/`;
}

/** Ответ PUT/DELETE — авторитетный блок матча (с questions): в кэш; 409 prediction_closed — перечитать. */
function predictionHandlers(queryClient: ReturnType<typeof useQueryClient>, id: number) {
  return {
    // после голоса сессия точно есть (гость заведён лениво) — кладём и под ключ «с сессией»
    onSuccess: (fixture: GameFixture) => {
      queryClient.setQueriesData({ queryKey: key(id) }, fixture);
      queryClient.setQueryData([...key(id), true], fixture);
      // очки, серия, «мои прогнозы» на вкладке «Игра» зависят от голоса — перечитать
      void queryClient.invalidateQueries({ queryKey: SCREEN_KEY });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'prediction_closed') {
        void queryClient.invalidateQueries({ queryKey: key(id) });
        void queryClient.invalidateQueries({ queryKey: SCREEN_KEY });
      }
    },
  };
}

/** Сервер по умолчанию (PUT без market) принимает исход матча — apps/game MARKET_WINNER. */
const DEFAULT_MARKET = 'winner';

/** PUT прогноза + prediction_submit {fixture_id, market, result}: ok, prediction_closed (409), машинная причина, network.
 *  a_predictions_* не уходит (events.SKIP); веха first_prediction — на первом ok (analytics.ts). */
export async function submitPrediction(id: number, market: string, body: Record<string, unknown>): Promise<GameFixture> {
  const submitted = (result: string) => track('prediction_submit', { fixture_id: String(id), market, result });
  try {
    const fixture = await apiRequest<GameFixture>('PUT', predictionPath(id), { auth: true, body });
    submitted('ok');
    return fixture;
  } catch (e) {
    if (e instanceof ApiError) submitted(predictionResult(e));
    throw e;
  }
}

/** Исход матча. market — GameFixture.market блока (у вызывающего); нет — из блока в кэше, иначе winner сервера. */
export function predictionMarket(queryClient: QueryClient, id: number, market?: string): string {
  return market
    ?? queryClient.getQueriesData<GameFixture | null>({ queryKey: key(id) }).map(([, f]) => f?.market).find(Boolean)
    ?? DEFAULT_MARKET;
}

export function usePredict(id: number, market?: string) {
  const queryClient = useQueryClient();
  const marketOf = () => predictionMarket(queryClient, id, market);
  return useMutation({
    mutationFn: (pick: string | null) => pick === null
      ? apiRequest<GameFixture>('DELETE', predictionPath(id), { auth: true })
      : submitPrediction(id, marketOf(), { pick, tz: deviceTimeZone() }),
    ...predictionHandlers(queryClient, id),
  });
}

/** Адрес снятия ответа: DELETE predictions/{id}/?market=<вопрос> (по умолчанию сервер снимает исход). */
export const withdrawPath = (id: number, market: string) => withParams(predictionPath(id), { market });

/** Ответ на вопрос к матчу (§2.18 этап 2): тот же PUT с {market, pick}; pick null — снять (DELETE ?market=). */
export function useAnswer(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ market, pick }: { market: string; pick: string | null }) => pick === null
      ? apiRequest<GameFixture>('DELETE', withdrawPath(id, market), { auth: true })
      : submitPrediction(id, market, { market, pick, tz: deviceTimeZone() }),
    ...predictionHandlers(queryClient, id),
  });
}

/** Тело ответа на овер: scoreboard и over — ровно те, что прислал сервер. */
export function overAnswerBody(q: GameOverQuestion, pick: string) {
  return { scoreboard: q.scoreboard, over: q.over, pick };
}

/** Ответ на вопрос про следующий овер (этап 2в): ответ — блок матча целиком (с новым live). 409 over_closed — перечитать. */
export function useOverAnswer(id: number, fixtureUrl?: string) {
  const queryClient = useQueryClient();
  const handlers = predictionHandlers(queryClient, id);
  return useMutation({
    mutationFn: ({ question, pick }: { question: GameOverQuestion; pick: string }) =>
      apiRequest<GameFixture>('POST', liveActionPath(fixtureUrl ?? fixturePath(id), 'over'), { auth: true, body: overAnswerBody(question, pick) }),
    onSuccess: handlers.onSuccess,
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) void queryClient.invalidateQueries({ queryKey: key(id) });
    },
  });
}

/** Пачка тапов «Болеем» → {accepted, cheer}. Без кэша: шкалу держит сам блок (ui/LiveGame). */
export function sendCheer(id: number, side: string, taps: number, fixtureUrl?: string) {
  return apiRequest<{ accepted: number; cheer: GameCheer }>('POST', liveActionPath(fixtureUrl ?? fixturePath(id), 'cheer'),
    { auth: true, body: { side, taps } });
}

/** Экран вкладки (адрес — url вкладки из config как есть). Без сессии — без личных блоков. */
export function useGameScreen(path: string) {
  const has = useHasSession();
  return useQuery({
    queryKey: [...SCREEN_KEY, path, has],
    queryFn: ({ signal }) => apiRequest<GameScreen>('GET', withParams(path, { tz: deviceTimeZone() }), { auth: has, signal }),
  });
}

/** Полная таблица лидеров: адрес — more_path сервера, свои параметры доклеиваем; страницы по next_cursor. */
export function useLeaderboard(path: string, period: string) {
  const has = useHasSession();
  return useInfiniteQuery({
    queryKey: ['game-leaderboard', path, period, has],
    queryFn: ({ pageParam, signal }) => apiRequest<Leaderboard>('GET',
      withParams(path.replace(/([?&])period=[^&]*&?/, '$1').replace(/[?&]$/, ''), { period, cursor: pageParam ?? undefined }),
      { auth: has, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? null,
  });
}

// ---- публичное имя в таблицах (§2.14): PATCH account/me/ {public_name}; "" — вернуть умолчание (имя + буква фамилии) ----
const ME = '/api/v5/mobile/account/me/';

export function useAccountMe() {
  const has = useHasSession();
  return useQuery({
    queryKey: ['account-me', has],
    queryFn: ({ signal }) => apiRequest<{ public_name: string; display_name: string; is_guest: boolean; leaderboard_name?: string }>('GET', ME, { auth: true, signal }),
    enabled: has,
    staleTime: 5 * 60_000,
  });
}

export function usePublicName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiRequest<{ public_name: string }>('PATCH', ME, { auth: true, body: { public_name: name } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['account-me'] });
      void queryClient.invalidateQueries({ queryKey: SCREEN_KEY });
      void queryClient.invalidateQueries({ queryKey: ['game-leaderboard'] });
    },
  });
}

/** Полный список матчей игры: адрес — more_path блока predict как есть. */
export function useGameFixtures(path: string) {
  const has = useHasSession();
  return useQuery({
    queryKey: ['game-fixtures', path, has],
    queryFn: ({ signal }) => apiRequest<{ items: GameFixture[]; has_more: boolean }>('GET', path, { auth: has, signal }),
  });
}

// ---- лиги друзей (§2.15) ----
const LEAGUES = `/api/v5/mobile/game/${SPORT}/leagues/`;
const leagueKey = (id: number) => ['friend-league', id];

export function useFriendLeague(id: number) {
  return useQuery({
    queryKey: leagueKey(id),
    queryFn: ({ signal }) => apiRequest<FriendLeague>('GET', `${LEAGUES}${id}/`, { auth: true, signal }),
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && count < 2,
  });
}

/** Мои лиги — адрес раздела «Гильдия» игрового мира как есть (ответ {sport, items}). Ключ — под экраном «Игра»:
 *  создание/вступление/выход уже перечитывают SCREEN_KEY, список гильдии обновится с ним. */
export function useMyLeagues(path: string) {
  return useQuery({
    queryKey: [...SCREEN_KEY, 'leagues', path],
    queryFn: ({ signal }) => apiRequest<{ items: FriendLeagueRow[] }>('GET', path, { auth: true, signal }),
  });
}

/** Создать (create_path блока) / вступить (join_path) — ответ — лига целиком; экран «Игра» перечитываем. */
export function useLeagueAction(path: string, kind: 'create' | 'join') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: string) => apiRequest<FriendLeague>('POST', path, { auth: true, body: kind === 'create' ? { name: value } : { code: value } }),
    onSuccess: (league) => {
      queryClient.setQueryData(leagueKey(league.id), league);
      void queryClient.invalidateQueries({ queryKey: SCREEN_KEY });
    },
  });
}

/** Владелец: переименовать / сменить код; ответ — лига. */
export function useUpdateLeague(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; rotate_code?: boolean }) => apiRequest<FriendLeague>('PATCH', `${LEAGUES}${id}/`, { auth: true, body }),
    onSuccess: (league) => {
      queryClient.setQueryData(leagueKey(id), league);
      void queryClient.invalidateQueries({ queryKey: SCREEN_KEY });
    },
  });
}

/** Выйти (member_id не задан) или удалить участника (владелец). */
export function useLeaveLeague(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId?: number) => apiRequest<unknown>('DELETE', memberId ? `${LEAGUES}${id}/members/${memberId}/` : `${LEAGUES}${id}/`, { auth: true }),
    onSuccess: (_, memberId) => {
      void queryClient.invalidateQueries({ queryKey: SCREEN_KEY });
      if (memberId) void queryClient.invalidateQueries({ queryKey: leagueKey(id) });
      else queryClient.removeQueries({ queryKey: leagueKey(id) });
    },
  });
}

// ---- жалоба на имя (§2.17): 202 {reported}, повтор безвреден; 400 report_invalid, 429 report_limit_reached ----
export type ReportTarget = { kind: 'player'; ref: string } | { kind: 'league'; league_id: number };

export function useReport() {
  return useMutation({
    mutationFn: (target: ReportTarget) => apiRequest<{ reported: boolean }>('POST', `/api/v5/mobile/game/${SPORT}/reports/`, { auth: true, body: target }),
  });
}
